import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import hero from "../assets/portfolio/hero.jpg";

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.15, delayChildren: 0.3 } },
};
const item = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

// Masked line-by-line reveal — the signature on-load moment.
const LineMask = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  // Subtle 3D parallax — image drifts up & gains tiny depth as we scroll past.
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "-18%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.2]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [1, 1.25]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "32%"]);

  return (
    <section
      ref={sectionRef}
      id="home"
      className="relative min-h-[100svh] flex items-end overflow-hidden"
    >
      {/* Background image */}
      <motion.div
        style={{ y: bgY, scale: bgScale }}
        className="absolute inset-0 will-change-transform"
      >
        <img
          src={hero}
          alt="FOXEL flagship residential render"
          className="w-full h-full object-cover"
        />
        {/* Radial vignette (per user PDF instruction) */}
        <motion.div
          style={{ opacity: overlayOpacity }}
          className="absolute inset-0"
        >
          <div
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(ellipse at center, rgba(0,0,0,0) 30%, rgba(0,0,0,0.55) 75%, rgba(0,0,0,0.85) 100%)",
            }}
          />
          {/* Bottom-to-top gradient for text contrast */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />
          {/* Left dark wash for headline legibility */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/10 to-transparent" />
        </motion.div>
      </motion.div>

      <motion.div
        style={{ y: contentY }}
        variants={container}
        initial="hidden"
        animate="show"
        className="relative z-10 w-full max-w-7xl mx-auto px-5 md:px-10 pb-20 md:pb-28 pt-32 will-change-transform"
      >
        <motion.span
          variants={item}
          className="inline-block text-[#e7b8b3] text-xs md:text-sm tracking-[0.35em] uppercase font-semibold mb-6"
        >
          Architectural Visualization Studio — Panvel, Navi Mumbai
        </motion.span>

        <motion.h1
          variants={item}
          className="text-white font-display font-black uppercase leading-[0.92] tracking-tight text-balance text-[2.4rem] sm:text-6xl lg:text-7xl xl:text-8xl max-w-5xl drop-shadow-[0_4px_24px_rgba(0,0,0,0.5)]"
        >
          <LineMask delay={0.55}>Architecture</LineMask>
          <LineMask delay={0.75}>
            <span className="brand-gradient-text">and</span> Interior
          </LineMask>
          <LineMask delay={0.95}>Design</LineMask>
        </motion.h1>

        <motion.p
          variants={item}
          className="mt-7 text-white/85 text-base md:text-lg leading-relaxed max-w-2xl drop-shadow-[0_2px_10px_rgba(0,0,0,0.6)]"
        >
          Bringing spaces to life with absolute precision. From striking high-rise residential
          towers to luxury minimalist interiors, we transform concepts into stunning, 4K
          photorealistic realities.
        </motion.p>

        <motion.div variants={item} className="mt-10 flex flex-col sm:flex-row gap-4">
          <button
            data-testid="hero-btn-about"
            onClick={() => scrollTo("about")}
            className="rounded-full px-8 py-4 backdrop-blur-md bg-white/10 border border-white/30 text-white hover:bg-white hover:text-[#4a1c40] transition-colors duration-300 uppercase tracking-widest text-xs font-bold"
          >
            About The Studio
          </button>
          <button
            data-testid="hero-btn-explore"
            onClick={() => scrollTo("portfolio")}
            className="group rounded-full px-8 py-4 brand-gradient text-white hover:opacity-90 transition-opacity duration-300 uppercase tracking-widest text-xs font-bold flex items-center justify-center gap-2"
          >
            Explore Our Work
            <ArrowRight
              size={16}
              className="group-hover:translate-x-1 transition-transform duration-300"
            />
          </button>
        </motion.div>
      </motion.div>

      <div className="absolute bottom-0 left-0 right-0 z-10 border-t border-white/15">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-4 flex items-center justify-between text-white/70 text-[11px] md:text-xs tracking-[0.3em] uppercase">
          <span>FOXEL — {new Date().getFullYear()}</span>
          <span className="hidden sm:block">Licensed Architects</span>
        </div>
      </div>
    </section>
  );
};
